import React from 'react'
import PropTypes from 'prop-types'
import { Typography, Box, Button, Drawer } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'

import {
  DrawerHeader,
  DrawerCard,
  DrawerEmptyState,
  DrawerSkeleton
} from '../DrawerComponents'
import DateRangeInput from '../DateRange'
import EnhancedMetricsSection from '../Metrics/EnhancedMetricsSection'
import EnhancedMetricsCharts from '../Metrics/EnhancedMetricsCharts'

import styles from './CampaignDrawerDetails.module.scss'

export const CampaignDrawerDetails = ({
  drawerOpen,
  onClose,
  selectedCampaign,
  campaignMetrics,
  loadingMetrics,
  startDate,
  endDate,
  onDateRangeChange
}) => {
  const { t } = useTranslation()

  const handleDateChange = (type, e) => {
    if (!onDateRangeChange) return
    const newDate = new Date(e.target.value)

    if (type === 'start') {
      onDateRangeChange(newDate, endDate)
    } else {
      onDateRangeChange(startDate, newDate)
    }
  }

  const handleViewCalls = () => {
    if (!selectedCampaign?.campaign_name || !startDate || !endDate) return
    const encodedName = encodeURIComponent(selectedCampaign.campaign_name)
    const startISO = startDate.toISOString()
    const endISO = endDate.toISOString()
    window.open(
      `/campaign/${encodedName}?start=${startISO}&end=${endISO}`,
      '_blank'
    )
  }

  return (
    <Drawer
      anchor='right'
      open={drawerOpen}
      onClose={onClose}
      disableEnforceFocus
      classes={{ paper: styles.drawerPaper }}
      ModalProps={{
        keepMounted: false,
        'aria-labelledby': 'campaign-drawer-title'
      }}
      PaperProps={{
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': 'campaign-drawer-title'
      }}
    >
      <DrawerHeader
        titleId='campaign-drawer-title'
        title={selectedCampaign?.campaign_name || t('campaigns.title')}
        subtitle={t('analytics.title')}
        icon='chart-line'
        onClose={onClose}
      />

      <Box className={styles.drawerContent}>
        {loadingMetrics && (
          <Box className={styles.cardGrid}>
            <DrawerCard title={t('analytics.dateRange.title')} icon='calendar-alt' fullWidth>
              <DrawerSkeleton width='100%' height={40} borderRadius={8} />
            </DrawerCard>
            <DrawerCard title={t('analytics.title')} icon='chart-bar' fullWidth>
              <DrawerSkeleton width='100%' height={120} borderRadius={8} />
            </DrawerCard>
          </Box>
        )}

        {!loadingMetrics && !campaignMetrics && (
          <DrawerEmptyState
            icon='chart-pie'
            text={t('campaigns.title')}
            subtext='Select a campaign to view its analytics.'
          />
        )}

        {!loadingMetrics && campaignMetrics && (
          <Box className={styles.cardGrid}>
            {/* Date Range Card */}
            <DrawerCard
              title={t('analytics.dateRange.title')}
              icon='calendar-alt'
              fullWidth
            >
              <DateRangeInput
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={(e) => handleDateChange('start', e)}
                onEndDateChange={(e) => handleDateChange('end', e)}
              />
              <Typography className={styles.dateRangeHint}>
                Metrics and charts will update automatically when you change the date range.
              </Typography>
            </DrawerCard>

            {/* Metrics Overview Card */}
            <DrawerCard
              title='Performance Metrics'
              icon='chart-bar'
              fullWidth
              collapsible
              defaultExpanded
            >
              <EnhancedMetricsSection
                metrics={campaignMetrics}
                metricsLoading={loadingMetrics}
                dateRangeStart={startDate}
                dateRangeEnd={endDate}
                today={new Date().toISOString()}
                planSize={campaignMetrics.planMinutes}
                planType={campaignMetrics.planType}
                planSizeLoading={false}
              />
            </DrawerCard>

            {/* Charts Card */}
            <DrawerCard
              title='Trends & Charts'
              icon='chart-area'
              fullWidth
              collapsible
              defaultExpanded
            >
              <EnhancedMetricsCharts
                lineChartData={{
                  callsPerDay: [],
                  handleTimePerDay: [],
                  cssPerDay: []
                }}
                metricsLoading={loadingMetrics}
              />
            </DrawerCard>

            {/* View Campaign Dashboard CTA */}
            <Box className={styles.ctaWrapper}>
              <Button
                fullWidth
                className={styles.callDashboardButton}
                onClick={handleViewCalls}
                disableRipple
              >
                <FontAwesomeIcon icon='external-link-alt' className={styles.ctaIcon} />
                {t('navigation.callDashboard')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  )
}

CampaignDrawerDetails.propTypes = {
  drawerOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedCampaign: PropTypes.object,
  campaignMetrics: PropTypes.object,
  loadingMetrics: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onDateRangeChange: PropTypes.func
}

export default CampaignDrawerDetails
